import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { AxiosError } from 'axios';

/**
 * Utility function to merge Tailwind CSS classes
 * Handles conflicts and removes duplicates
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency with proper localization.
 *
 * [T40P0] Changed defaults so monetary values respect:
 *   • The user's preferred currency (IDR → "Rp", JPY → "¥", USD → "$", etc.)
 *   • The browser's system locale for number grouping/decimal separators
 *     (id-ID: "Rp 1.500.000", en-US: "$1,500.00", de-DE: "1.500,00 €")
 *   • Currency-appropriate decimal places — IDR/JPY naturally have 0 decimal
 *     places; USD/EUR have 2. Forcing minimumFractionDigits=2 was wrong for
 *     zero-decimal currencies and is removed.
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = typeof navigator !== 'undefined' ? navigator.language : 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format number with thousands separators
 */
export function formatNumber(
  value: number,
  decimals: number = 0,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format date to locale string
 */
export function formatDate(
  date: string | Date,
  format: 'short' | 'medium' | 'long' = 'medium',
  locale: string = 'en-US'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const formatOptions: Record<string, Intl.DateTimeFormatOptions> = {
    short: { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
  };

  return new Intl.DateTimeFormat(locale, formatOptions[format]).format(dateObj);
}

/**
 * Format relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
}

/**
 * Calculate percentage
 */
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return (value / total) * 100;
}

/**
 * Calculate percentage change
 */
export function calculatePercentageChange(
  oldValue: number,
  newValue: number
): number {
  if (oldValue === 0) return 0;
  return ((newValue - oldValue) / Math.abs(oldValue)) * 100;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Generate initials from name
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Extract an API error message from an unknown catch value.
 * Works with Axios errors (response.data.{message|error}),
 * ZodError ({name: 'ZodError', issues: [{message, path}]}),
 * nested response.data.data.{message|error} shapes, and generic Errors.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    // Axios-shaped error: response.data.{message|error} or response.data.data.{message|error}
    if ('response' in error) {
      const axiosErr = error as AxiosError<{ message?: string; error?: string; data?: { message?: string; error?: string } } | string>;
      const apiData = axiosErr.response?.data;
      if (typeof apiData === 'string' && apiData.trim()) return apiData;
      if (apiData && typeof apiData === 'object') {
        if (typeof apiData.message === 'string' && apiData.message) return apiData.message;
        // Hono zValidator envelope: { success:false, error: ZodError | string }
        if (typeof apiData.error === 'string' && apiData.error) return apiData.error;
        if (apiData.error && typeof apiData.error === 'object') {
          const zodFromEnvelope = extractZodMessage(apiData.error);
          if (zodFromEnvelope) return zodFromEnvelope;
        }
        // Nested: response.data.data.{message|error}
        const nested = apiData.data;
        if (nested && typeof nested === 'object') {
          if (typeof nested.message === 'string' && nested.message) return nested.message;
          if (typeof nested.error === 'string' && nested.error) return nested.error;
        }
      }
    }
    // ZodError shape: { name: 'ZodError', issues: [{ message, path, code }, ...] }
    const topLevelZod = extractZodMessage(error);
    if (topLevelZod) return topLevelZod;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Pull the first useful message from a ZodError-shaped value, if any.
 * Accepts either `{name:'ZodError', issues:[...]}` directly, or any object
 * that has an `issues` array (the Hono zValidator envelope nests it under
 * `response.data.error`).
 */
function extractZodMessage(candidate: unknown): string | null {
  if (!candidate || typeof candidate !== 'object') return null;
  const obj = candidate as { name?: unknown; issues?: unknown };
  if (obj.name !== 'ZodError') return null;
  if (!Array.isArray(obj.issues) || obj.issues.length === 0) return null;
  const first = obj.issues[0] as { message?: unknown; path?: unknown };
  if (typeof first.message !== 'string' || !first.message) return null;
  const path = Array.isArray(first.path) && first.path.length > 0 ? first.path.join('.') + ': ' : '';
  return `${path}${first.message}`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Deep clone object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if value is empty
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
}

/**
 * Generate random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Get account type label
 */
export function getAccountTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    checking: 'Checking',
    savings: 'Savings',
    credit_card: 'Credit Card',
    investment: 'Investment',
    loan: 'Loan',
    mortgage: 'Mortgage',
    manual: 'Manual',
    crypto: 'Crypto',
    other: 'Other',
  };
  return labels[type] || type;
}

/**
 * Get transaction type label
 */
export function getTransactionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    income: 'Income',
    expense: 'Expense',
    transfer: 'Transfer',
  };
  return labels[type] || type;
}

/**
 * Get budget method label
 */
export function getBudgetMethodLabel(method: string): string {
  const labels: Record<string, string> = {
    'zero-based': 'Zero-Based',
    'spending-plan': 'Spending Plan',
    'rollover': 'Rollover',
  };
  return labels[method] || method;
}

/**
 * Get goal type label
 */
export function getGoalTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    savings: 'Savings',
    debt_payoff: 'Debt Payoff',
    investment: 'Investment',
    other: 'Other',
  };
  return labels[type] || type;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Calculate days until date
 */
export function daysUntil(date: string | Date): number {
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  const diffInMs = targetDate.getTime() - today.getTime();
  return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
}

/**
 * Get color for budget status
 */
export function getBudgetStatusColor(spent: number, budgeted: number): string {
  const percentage = (spent / budgeted) * 100;
  if (percentage >= 100) return 'hsl(var(--destructive))';
  if (percentage >= 80) return 'hsl(var(--chart-4))';
  return 'hsl(var(--chart-2))';
}

/**
 * Currency-aware quick-amount presets for contribution dialogs.
 *
 * Returns denominations that feel natural for the given currency's fraction
 * size and real-world usage. Zero-decimal currencies (JPY, IDR) use larger
 * whole-number increments; standard 2-decimal currencies use smaller values.
 *
 * [T58P0] Contribution dialog currency-aware presets
 */
export function getCurrencyQuickAmounts(currency: string): number[] {
  switch (currency) {
    // ── Zero-decimal / large-unit currencies ──
    case 'IDR':
      return [1000, 2000, 5000, 10000, 20000, 50000, 100000];
    case 'JPY':
      return [500, 1000, 2000, 5000, 10000, 50000];
    case 'KRW':
      return [1000, 5000, 10000, 50000, 100000];
    case 'VND':
      return [10000, 50000, 100000, 200000, 500000, 1000000];

    // ── Standard 2-decimal currencies ──
    case 'USD':
    case 'CAD':
    case 'AUD':
    case 'SGD':
      return [25, 50, 100, 250, 500, 1000];
    case 'EUR':
      return [25, 50, 100, 250, 500, 1000];
    case 'GBP':
      return [20, 50, 100, 200, 500, 1000];

    // ── Fallback for unknown currencies ──
    default:
      return [25, 50, 100, 250, 500, 1000];
  }
}

/**
 * Get the currency symbol for a currency code.
 * Uses Intl.NumberFormat to extract the symbol for the locale.
 *
 * [T58P0] Dynamic currency symbol for forms and labels
 */
export function getCurrencySymbol(
  currency: string,
  locale: string = typeof navigator !== 'undefined' ? navigator.language : 'en-US',
): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || currency;
  } catch {
    return currency;
  }
}

/**
 * Maps snake_case AccountType enum values to human-readable display strings.
 * [T69P1-b] Consistent with the humanized labels already used in AccountForm.
 */
const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  checking: 'Checking',
  savings: 'Savings',
  credit_card: 'Credit Card',
  investment: 'Investment',
  loan: 'Loan',
  mortgage: 'Mortgage',
  manual: 'Manual',
  crypto: 'Crypto',
  other: 'Other',
};

export function humanizeAccountType(type: string): string {
  return ACCOUNT_TYPE_LABELS[type] ?? type;
}

/**
 * Get the step increment for a currency's input field.
 * Zero-decimal currencies use step=1; standard currencies use step=0.01.
 *
 * [T58P0] Currency-aware input step
 */
export function getCurrencyStep(currency: string): string {
  const zeroDecimal = ['JPY', 'IDR', 'KRW', 'VND', 'CLP', 'ISK', 'UGX', 'RWF'];
  return zeroDecimal.includes(currency) ? '1' : '0.01';
}

/**
 * Get the minimum input value appropriate for a currency.
 */
export function getCurrencyMinAmount(currency: string): string {
  const zeroDecimal = ['JPY', 'IDR', 'KRW', 'VND', 'CLP', 'ISK', 'UGX', 'RWF'];
  return zeroDecimal.includes(currency) ? '1' : '0.01';
}
